<template>
  <div class="init-new-order" data-cy="order-type-form">
    <div class="form-cnt">
      <p v-if="isReOrderDisabled">{{ $t('createOrdersPopup.disallowed_order_text') }}</p>
      <div v-if="!loading && isAccountOnHold" class="subtitle">
        {{ $t('viewOrder.submit_button_disabled_acc_onhold') }}
        <a
          v-if="accountReceivablesEmail"
          :href="`mailto:${accountReceivablesEmail}?subject=Order submission issue`"
          target="_blank">
          {{ accountReceivablesEmail }}
        </a>
      </div>
      <div class="form full-fields">
        <div v-if="loading" class="loader"></div>
        <div v-else class="form-grid">
          <div v-if="isSalesRep()" class="fields">
            <div class="field-new">
              <label for="init-new-order-contact">{{ $t('createOrdersPopup.contacts_title') }}</label>
              <select
                id="init-new-order-contact"
                v-model="initOrderModel.contactId"
                class="with-arrow"
                v-on:change="onChangeContact">
                <option disabled selected value="">-</option>
                <option v-for="contact in draftInitInfo?.userContacts" :key="contact.id" :value="contact.id">
                  {{ `${contact.firstName} ${contact.lastName}${hasBuyerFunction(contact) ? ' (Buyer)' : ''}` }}
                </option>
              </select>
              <div v-if="selectedContact" class="contact-info">
                <div>
                  <span class="title">{{ $t('createOrdersPopup.contacts_roles') }}</span>
                  <span class="description">{{ contactFunctions(selectedContact) }}</span>
                </div>
                <div>
                  <span class="title">{{ $t('createOrdersPopup.contacts_email') }}</span>
                  <span class="description">
                    <a :href="`mailto:${selectedContact.email}`">{{ selectedContact.email }} </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!isOrderFromAvailability" class="fields calendar-override">
            <div class="field-new">
              <label for="init-new-order-type-selector">{{ $t('createOrdersPopup.order_types_title') }}</label>
              <select
                id="init-new-order-type-selector"
                v-model="initOrderModel.orderTypeId"
                class="with-arrow"
                data-cy="order-type-selector"
                v-on:change="onChangeOrderType">
                <option v-for="orderType in enabledOrderTypes" :key="orderType" :value="orderType.toLocaleLowerCase()">
                  {{ getOrderTypeLabel(orderType) }}
                </option>
              </select>
            </div>
            <BackstageInputCalendar
              name="init-new-order-requested-date"
              v-model="initOrderModel.startDate"
              :disabled="rrdSelectionDisabled()"
              :label="$t('createOrdersPopup.shipping_date')"
              :max-date="maximumDate"
              :minDate="minimumDate"
              data-cy="requested-date-picker">
            </BackstageInputCalendar>
          </div>
        </div>
        <div class="buttons-cnt buttons-cnt-divisor full-width">
          <div class="fields">
            <div class="field">
              <BackstageButton
                :variant="ButtonVariant.SECONDARY"
                data-cy="cancel-button-cnt"
                data-cy-button="cancel-button"
                full-width
                v-on:click="close">
                {{ $t('createOrdersPopup.cancel_button') }}
              </BackstageButton>
            </div>
            <div class="field">
              <BackstageButton
                data-cy="create-button-cnt"
                data-cy-button="create-button"
                :disabled="!submitButtonEnabled()"
                full-width
                v-on:click="initOrder()">
                {{ createButtonLabel }}
              </BackstageButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import { useAnalytics } from '@/composables/analytics';
import { AccountsReceivablesEmails } from '@/constants/accounts-receivables.constants';
import { globalConstants } from '@/constants/global.constants';
import { DISALLOWED_REORDER_LEGAL_ENTITIES } from '@/constants/order.constants';
import { localeMixins } from '@/mixins/locale.mixins';
import { ButtonVariant } from '@/models/button-variant.enum';
import { SFContactModel } from '@/models/sf-contact.model';
import { AccountsService } from '@/services/accounts.service';
import { FeaturesService } from '@/services/features.service';
import {
  AccountDetailsResponseDto,
  ContactModel,
  DraftInitInfoDto,
  LegalEntityEnum,
  OrderType,
  OrderTypeDto,
} from '@onrunning/backstage-models';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useFormat } from '../../composables/format';
import { AddEditOrderBreadcrumbSteps } from '../../models/add-edit-order-breadcrumb-steps.enum';
import { GaCheckoutStepNames, GaCustomEvents } from '../../models/ga-custom-events.enum';
import { InitOrderModel } from '../../models/init-order.model';
import { DraftsService } from '../../services/drafts.service';
import { useUserStore } from '../../stores/user';

export default defineComponent({
  name: 'init-new-order',
  components: {
    BackstageInputCalendar,
    BackstageButton,
  },
  props: {
    isOrderFromAvailability: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup() {
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
    const { isSalesRep, hasBuyerFunction } = useUserStore();
    const { formatDateToIsoStringIgnoringTime } = useFormat();
    const { addCustomGaEvent } = useAnalytics();

    return {
      accountId: selectedAccountId,
      selectedAccount,
      addCustomGaEvent,
      ButtonVariant,
      isSalesRep,
      hasBuyerFunction,
      formatDateToIsoStringIgnoringTime,
    };
  },
  data() {
    return {
      draftsService: new DraftsService(),
      accountsService: new AccountsService(),
      selectedContact: undefined as ContactModel | undefined,
      initOrderModel: new InitOrderModel(),
      draftInitInfo: undefined as DraftInitInfoDto | undefined,
      accountReceivablesEmail: undefined as string | undefined,
      isAccountOnHold: false,
      disabledDates: (() => {
        return true;
      }) as (date: Date) => boolean,
      showMatrix: false,
      loading: true,
      maximumDate: globalConstants.maximumDate,
      createButtonLabel: this.$t('createOrdersPopup.create_button'),
      minimumDate: moment().toDate(),
    };
  },
  mounted() {
    this.getInitInfo();

    this.addCustomGaEvent({
      event: GaCustomEvents.CheckoutStep1,
      checkout_step_name: GaCheckoutStepNames.SelectOrderType,
    });
  },
  computed: {
    isReOrderDisabled(): boolean {
      return (
        this.initOrderModel.orderTypeId === OrderType.ReOrder.toLocaleLowerCase() &&
        DISALLOWED_REORDER_LEGAL_ENTITIES.includes(this.selectedAccount?.legalEntity as LegalEntityEnum)
      );
    },
    selectedOrderTypeDto(): OrderTypeDto {
      return this.draftInitInfo?.orderTypes.find(
        (type) => type.type.toLocaleLowerCase() === this.initOrderModel.orderTypeId,
      ) as OrderTypeDto;
    },
    enabledOrderTypes(): OrderType[] {
      return this.draftInitInfo?.orderTypes
        .map((type) => type.type)
        .filter((type) =>
          FeaturesService.enabledShipDatesGrouping ? type !== OrderType.MultiplePreOrder : type,
        ) as OrderType[];
    },
  },
  methods: {
    async getInitInfo() {
      this.draftInitInfo = (await this.draftsService.getInitInfo(this.accountId)).model;

      if (!this.draftInitInfo) {
        return this.$snotify.error(this.$t('global.error'));
      }

      await this.checkAccountStatus();

      this.initOrderModel.orderTypeId = this.draftInitInfo.orderTypes[0].type.toLocaleLowerCase();
      this.initOrderModel.startDate = moment.parseZone(this.draftInitInfo.orderTypes[0].defaultDate).toDate();
      this.minimumDate = moment.parseZone(this.draftInitInfo.orderTypes[0].startDate).toDate();

      this.loading = false;
    },
    async checkAccountStatus() {
      const accountDetails: AccountDetailsResponseDto | undefined = (
        await this.accountsService.getAccountDetails(this.accountId)
      )?.model;

      if (!accountDetails) {
        return this.$snotify.error(this.$t('global.error'));
      }

      if (accountDetails.onHold) {
        this.isAccountOnHold = true;
        this.accountReceivablesEmail = AccountsReceivablesEmails[accountDetails.legalEntity];
        this.createButtonLabel = this.$t('createOrdersPopup.create_draft_button');
      }
    },
    contactFunctions(contact: SFContactModel): string {
      if (contact.userFunction) {
        return contact.userFunction.join(', ');
      } else {
        return this.$t('createOrdersPopup.contact_has_no_roles').toString();
      }
    },
    onChangeOrderType() {
      const selectedOrderType = this.selectedOrderTypeDto;

      this.initOrderModel.startDate = moment.parseZone(selectedOrderType.defaultDate).toDate();
      this.minimumDate = moment.parseZone(selectedOrderType.startDate).toDate();

      this.maximumDate = selectedOrderType.endDate
        ? moment.parseZone(selectedOrderType.endDate).toDate()
        : globalConstants.maximumDate;
    },
    onChangeContact() {
      this.selectedContact = this.draftInitInfo?.userContacts.find(
        (contact) => contact.id === this.initOrderModel.contactId,
      );
    },
    submitButtonEnabled(): boolean {
      return (
        !this.isReOrderDisabled &&
        !!this.initOrderModel.orderTypeId &&
        (!this.isSalesRep() || !!this.initOrderModel.contactId)
      );
    },
    rrdSelectionDisabled(): boolean {
      return FeaturesService.enabledShipDatesGrouping && this.selectedOrderTypeDto.type === OrderType.PreOrder;
    },
    getOrderTypeLabel(orderType: OrderType): string {
      const title = localeMixins.getLocaleTextByOrderType('createOrdersPopup.order_type_pick_list_label_', orderType);

      return this.$t(title).toString();
    },
    initOrder() {
      this.$emit('close');

      if (this.isOrderFromAvailability) {
        this.$emit('create-order', { contactId: this.initOrderModel.contactId });

        return;
      }

      const orderType = this.initOrderModel.orderTypeId;
      const startDate = this.formatDateToIsoStringIgnoringTime(this.initOrderModel.startDate);
      const step = AddEditOrderBreadcrumbSteps.productsModelsSelection;

      let url = `/orders/create/${orderType}/${startDate}`;

      if (this.initOrderModel.contactId) {
        url += `/${this.initOrderModel.contactId}`;
      }

      url += `/${step}`;

      this.$router.push(url);
    },
    close() {
      this.$emit('close');
    },
  },
});
</script>
<style scoped lang="scss">
.init-new-order {
  min-width: 525px;
  .form-cnt {
    .subtitle {
      text-align: left;
      gap: 10px;
      padding-top: -10px;
      margin-bottom: 20px;
      max-width: 500px;
      color: var(--primary-text-bold-color);

      ::v-deep(a) {
        text-decoration: underline;
      }
    }
    .loader {
      width: 532px;
    }
    .form-grid {
      display: grid;
      gap: 10px;
    }

    .contact-info {
      padding: 5px 10px;
      margin-top: 5px;
      background: var(--read-only-field-bg-color);
      color: var(--primary-text-bold-color);

      div {
        margin-bottom: 2px;
        display: flex;
        span.title {
          width: 50px;
        }
        span.description {
          font-weight: 600;
        }
      }
    }
  }

  .calendar-override:deep() {
    .dp__menu {
      position: fixed !important;
    }
    .dp--menu-wrapper {
      // needs this given .popup-cnt .popup .popup-content { overflow: auto } not working on safari
      position: static;
    }
  }
}
</style>
